import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import XIcon from '@heroicons/react/outline/XIcon';
import SearchIcon from '@heroicons/react/solid/SearchIcon';
import { useHistory } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import { SearchEntityType } from '@tribeplatform/gql-client/types';
import { useSearch } from '@tribeplatform/react-sdk/hooks';
import { getSearchKey } from '@tribeplatform/react-sdk/utils/keys';
import { Autocomplete } from '@tribeplatform/react-ui-kit/Autocomplete';
import { Button } from '@tribeplatform/react-ui-kit/Button';
import { SpinnerIcon } from '@tribeplatform/react-ui-kit/icons';
import { Modal } from '@tribeplatform/react-ui-kit/Modal';
import { useDebounce } from '../common/hooks/useDebounce';
import { T, useI18n } from '../i18n';
import { cleanSearchQuery } from '../utils/search';
import { SearchAutocompleteItems } from './SearchAutocompleteItems';
import { SearchRowPage } from './SearchRow';
import { getSearchItemLink, getSearchPageLink } from './utils';
const PAGE_RESULT = 'search-page-shortcut';
export const SearchModal = ({ open = false, onClose, autoFocus = false }) => {
    const intl = useI18n();
    const [dirtyQuery, setDirtyQuery] = useState('');
    const query = cleanSearchQuery(dirtyQuery);
    const history = useHistory();
    const debouncedQuery = useDebounce(query, 300);
    const { data, isLoading, isFetching } = useSearch({
        variables: {
            input: {
                includeExternal: true,
                query: debouncedQuery,
            },
        },
        fields: 'all',
        useQueryOptions: {
            queryKey: getSearchKey({
                variables: {
                    input: { query: debouncedQuery },
                },
            }),
            // keepPreviousData: true,
        },
    });
    const onSelect = (value) => {
        onClose();
        if (value && value.entityType !== SearchEntityType.External) {
            if (value === PAGE_RESULT) {
                history.push(getSearchPageLink({ search: dirtyQuery }));
            }
            else {
                history.push(getSearchItemLink(value));
            }
        }
    };
    const onEnter = ({ highlightedIndex, inputValue }) => {
        if (inputValue && highlightedIndex < 1) {
            history.push(getSearchPageLink({ search: dirtyQuery }));
        }
    };
    return (_jsxs(Modal, { open: open, onClose: onClose, children: [_jsxs(Autocomplete, { itemToString: value => { var _a; return ((_a = value) === null || _a === void 0 ? void 0 : _a.title) || ''; }, value: dirtyQuery, onChange: onSelect, options: [
                    PAGE_RESULT,
                    ...((data === null || data === void 0 ? void 0 : data.hits.flatMap(category => category.hits)) || []),
                ], onInputChange: query => {
                    setDirtyQuery(query);
                }, loading: isLoading, clearInputOnSelect: true, children: [_jsxs("div", { className: "h-16 px-4 flex justify-between items-center bg-background space-s-2", children: [_jsx("div", { className: "grow", children: _jsx(Autocomplete.Input, { autoFocus: autoFocus, leadingIcon: isFetching ? (_jsx(SpinnerIcon, { className: "animate-spin" }, void 0)) : (_jsx(SearchIcon, {}, void 0)), placeholder: intl.$t({
                                        id: 'Generics.SearchDotDotDot',
                                        defaultMessage: 'Search...',
                                    }), onEnter: onEnter, value: dirtyQuery, showClearButton: false, inputMode: "search" }, void 0) }, void 0), _jsx("button", { type: "button", className: "flex-shrink-0 p-2", onClick: onClose, children: _jsx(XIcon, { className: "h-5 w-5", "aria-hidden": "true" }, void 0) }, void 0)] }, void 0), _jsxs(Autocomplete.Items, { className: "bg-surface overflow-auto mb-10", variant: "unstyled", style: { maxHeight: 'calc(100vh - 64px - 48px)' }, children: [_jsx(Autocomplete.Item, { index: 0, value: "search-page-shortcut-value", children: _jsx(SearchRowPage, { search: dirtyQuery, link: getSearchPageLink({ search: dirtyQuery }), spacing: "sm" }, void 0) }, void 0), _jsx(SearchAutocompleteItems, { data: data }, void 0), isLoading && (_jsx(Autocomplete.ItemsEmpty, { children: _jsx(T, { id: "Generics.SearchingDotDotDto", defaultMessage: "Searching..." }, void 0) }, void 0))] }, void 0)] }, void 0), (data === null || data === void 0 ? void 0 : data.totalCount) > 0 && (_jsx("div", { className: "fixed inset-x-0 bottom-0", children: _jsx(Button, { variant: "primary", as: RouterLink, to: getSearchPageLink({ search: dirtyQuery }), onClick: () => onClose(), fullWidth: true, className: "rounded-none h-12", children: _jsx(T, { id: "Search.SearchModals.ViewAllResultsButtonText", defaultMessage: "View all {total_count} results", values: { total_count: data === null || data === void 0 ? void 0 : data.totalCount } }, void 0) }, void 0) }, void 0))] }, void 0));
};
//# sourceMappingURL=SearchModal.js.map